"use client";

import Link from "next/link";
import {
  Heart,
  Home,
  LayoutGrid,
  Search,
  User,
} from "lucide-react";
import { cn } from "@/lib/utils";
import {
  mobileBottomNavBarClassName,
  mobileBottomNavFavCountBadgeClassName,
  mobileBottomNavInnerClassName,
  mobileBottomNavItemClassName,
} from "@/lib/mobile-bottom-nav";

export type MarketplaceMobileBottomNavProps = {
  favCount: number;
  profilHref: string;
  onGoToServices: () => void;
  onOpenFavorites: () => void;
  onOpenSearch: () => void;
  /** Homepage: Početna scrolls to top and is active */
  homeActive?: boolean;
  profilActive?: boolean;
  homeHref?: string;
  className?: string;
};

/** Marketplace dock — same markup as `ServicesSection` homepage bottom nav. */
export function MarketplaceMobileBottomNav({
  favCount,
  profilHref,
  onGoToServices,
  onOpenFavorites,
  onOpenSearch,
  homeActive = false,
  profilActive = false,
  homeHref = "/",
  className,
}: MarketplaceMobileBottomNavProps) {
  return (
    <nav
      className={cn(mobileBottomNavBarClassName, className)}
      aria-label="Mobilna navigacija usluga"
    >
      <div className={cn(mobileBottomNavInnerClassName, "grid-cols-5")}>
        <button
          type="button"
          onClick={onGoToServices}
          className={mobileBottomNavItemClassName(false)}
          aria-label="Idi na listu usluga"
        >
          <LayoutGrid className="h-5 w-5 shrink-0" />
          <span className="max-w-full truncate text-center text-[10px] font-medium">
            Usluge
          </span>
        </button>

        <button
          type="button"
          onClick={onOpenFavorites}
          className={mobileBottomNavItemClassName(false)}
          aria-label={`Otvori sačuvane usluge${
            favCount > 0 ? ` (${favCount})` : ""
          }`}
        >
          <Heart
            className={cn("h-5 w-5 shrink-0", favCount > 0 && "fill-current")}
          />
          {favCount > 0 && (
            <span className={mobileBottomNavFavCountBadgeClassName}>
              {favCount > 99 ? "99+" : favCount}
            </span>
          )}
          <span className="max-w-full truncate text-center text-[10px] font-medium">
            Sačuvani
          </span>
        </button>

        {homeActive ? (
          <button
            type="button"
            onClick={() => {
              window.scrollTo({ top: 0, behavior: "smooth" });
            }}
            className={mobileBottomNavItemClassName(true)}
            aria-label="Početna stranica"
            aria-current="page"
          >
            <Home className="h-5 w-5 shrink-0" />
            <span className="max-w-full truncate text-center text-[10px] font-medium">
              Početna
            </span>
          </button>
        ) : (
          <Link
            href={homeHref}
            className={mobileBottomNavItemClassName(false)}
            aria-label="Početna stranica"
          >
            <Home className="h-5 w-5 shrink-0" />
            <span className="max-w-full truncate text-center text-[10px] font-medium">
              Početna
            </span>
          </Link>
        )}

        <Link
          href={profilHref}
          className={mobileBottomNavItemClassName(profilActive)}
          aria-label="Otvori profil"
          aria-current={profilActive ? "page" : undefined}
        >
          <User className="h-5 w-5 shrink-0" />
          <span className="max-w-full truncate text-center text-[10px] font-medium">
            Profil
          </span>
        </Link>

        <button
          type="button"
          onClick={onOpenSearch}
          className={mobileBottomNavItemClassName(false)}
          aria-label="Otvori pretragu"
        >
          <Search className="h-5 w-5 shrink-0" />
          <span className="max-w-full truncate text-center text-[10px] font-medium">
            Pretraga
          </span>
        </button>
      </div>
    </nav>
  );
}
